/**
 * Configuration — Classification (groupings landing / family board) + Advanced (membership,
 * attendance bands, timezone — shown only to roles that can save them, WSRADM-0048).
 * One page instance: unsaved membership edits survive a tab switch.
 * Tenant from the JWT. Structure Save is not the membership Save.
 */
import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  Skeleton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import {
  getAttendanceBands,
  type AttendanceBandsDto,
} from '@/api/configuration/attendanceBands';
import { getMembershipConfig, type MembershipConfigDto } from '@/api/configuration/membershipConfig';
import { getDimensions } from '@/api/dimensions/dimensionsApi';
import type { DimensionStructure, DimensionStructureSaved } from '@/api/dimensions/types';
import { useAuth } from '@/auth/AuthContext';
import { canEditTenantSettings } from '@/auth/settingsAccess';
import { ErrorDetails } from '@/components/ErrorDetails';
import { useCopy } from '@/hooks/useCopy';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useResource } from '@/hooks/useResource';
import { AttendanceBandsSettings } from './AttendanceBandsSettings';
import { detailsCode } from './detailsCode';
import { FamilyBoard } from './FamilyBoard';
import { GroupingsLanding } from './GroupingsLanding';
import { MembershipSettings } from './MembershipSettings';
import { TimezoneSettings } from './TimezoneSettings';

type ConfigTab = 'classification' | 'advanced';

function readTab(raw: string | null, canEditSettings: boolean): ConfigTab {
  return raw === 'advanced' && canEditSettings ? 'advanced' : 'classification';
}

export function ConfigurationPage() {
  const { t } = useCopy();
  useDocumentTitle(t('page.configuration.title', 'Configuration'));
  const [searchParams, setSearchParams] = useSearchParams();
  const { operator } = useAuth();
  const canEditSettings = canEditTenantSettings(operator?.roles);
  const rawTab = searchParams.get('tab');
  const tab = readTab(rawTab, canEditSettings);
  const dimensionParam = searchParams.get('dimension');
  const [visitedAdvanced, setVisitedAdvanced] = useState(tab === 'advanced');
  const [structure, setStructure] = useState<DimensionStructure | null>(null);
  const [membershipUnknown, setMembershipUnknown] = useState(false);
  const [boardDirty, setBoardDirty] = useState(false);
  const [boardKey, setBoardKey] = useState(0);
  const [pendingTab, setPendingTab] = useState<ConfigTab | null>(null);

  useEffect(() => {
    if (tab === 'advanced') setVisitedAdvanced(true);
  }, [tab]);

  const structureRes = useResource(() => getDimensions(), []);

  useEffect(() => {
    if (structureRes.data) setStructure(structureRes.data);
  }, [structureRes.data]);

  const structureUnknown = detailsCode(structureRes.error) === 'UNKNOWN_TENANT';
  const unknownTenant = structureUnknown || membershipUnknown;

  function setTab(value: ConfigTab) {
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next, { replace: true });
  }

  // A non-writer's `?tab=advanced` deep link lands on Classification; the URL
  // is rewritten so it never names a destination the role cannot see (PERM-004).
  useEffect(() => {
    if (rawTab === 'advanced' && !canEditSettings) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', 'classification');
          return next;
        },
        { replace: true },
      );
    }
  }, [rawTab, canEditSettings, setSearchParams]);

  function setParam(key: string, value: string | null) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === null) next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );
  }

  function onStructureSaved(next: DimensionStructureSaved) {
    setStructure({ dimensions: next.dimensions });
  }

  function onTabChange(value: ConfigTab) {
    if (value !== tab && boardDirty) {
      setPendingTab(value);
      return;
    }
    setTab(value);
  }

  return (
    <Box>
      <Typography variant="h1" component="h1" sx={{ mb: 2.5 }}>
        {t('page.configuration.title', 'Configuration')}
      </Typography>

      {unknownTenant ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('config.unknownTenant', 'This session has no tenant. Sign in again.')}
        </Alert>
      ) : null}

      <Tabs value={tab} onChange={(_event, value: ConfigTab) => onTabChange(value)} sx={{ mb: 3 }}>
        <Tab value="classification" label={t('config.tab.classification', 'Classification')} />
        {canEditSettings ? (
          <Tab value="advanced" label={t('config.tab.advanced', 'Advanced')} />
        ) : null}
      </Tabs>

      <Box hidden={tab !== 'classification'}>
        {dimensionParam === null ? (
          <GroupingsLanding
            structure={structure}
            structureStatus={structureRes.status}
            structureError={structureRes.error}
            refetchStructure={structureRes.refetch}
            onStructureSaved={onStructureSaved}
            onOpen={(slug) => setParam('dimension', slug)}
            unknownTenant={structureUnknown}
          />
        ) : (
          <FamilyBoard
            key={`${dimensionParam}-${boardKey}`}
            slug={dimensionParam}
            structure={structure}
            structureStatus={structureRes.status}
            refetchStructure={structureRes.refetch}
            onStructureSaved={onStructureSaved}
            onBack={() => setParam('dimension', null)}
            onDirtyChange={setBoardDirty}
          />
        )}
      </Box>

      {canEditSettings && visitedAdvanced ? (
        <Box hidden={tab !== 'advanced'}>
          <AdvancedPanel onUnknownTenant={setMembershipUnknown} />
        </Box>
      ) : null}

      <Dialog open={pendingTab !== null} onClose={() => setPendingTab(null)}>
        <DialogTitle>{t('common.unsavedChanges', 'You have unsaved changes.')}</DialogTitle>
        <DialogActions>
          <Button onClick={() => setPendingTab(null)}>{t('common.stay', 'Stay')}</Button>
          <Button
            onClick={() => {
              if (pendingTab === null) return;
              setBoardKey((n) => n + 1);
              setTab(pendingTab);
              setPendingTab(null);
            }}
          >
            {t('common.leave', 'Leave')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function AdvancedPanel({
  onUnknownTenant,
}: {
  onUnknownTenant: (unknown: boolean) => void;
}) {
  const { t } = useCopy();
  const [timezoneUnknown, setTimezoneUnknown] = useState(false);
  const [membership, setMembership] = useState<MembershipConfigDto | null>(null);
  const [bands, setBands] = useState<AttendanceBandsDto | null>(null);
  const membershipRes = useResource(() => getMembershipConfig(), []);
  const bandsRes = useResource(() => getAttendanceBands(), []);

  useEffect(() => {
    if (membershipRes.data) setMembership(membershipRes.data);
  }, [membershipRes.data]);

  useEffect(() => {
    if (bandsRes.data) setBands(bandsRes.data);
  }, [bandsRes.data]);

  const membershipUnknown = detailsCode(membershipRes.error) === 'UNKNOWN_TENANT';
  const bandsUnknown = detailsCode(bandsRes.error) === 'UNKNOWN_TENANT';
  const advancedUnknown = membershipUnknown || bandsUnknown || timezoneUnknown;
  useEffect(() => {
    onUnknownTenant(advancedUnknown);
    return () => onUnknownTenant(false);
  }, [advancedUnknown, onUnknownTenant]);

  const membershipLoading = membershipRes.status === 'loading' && membership === null;
  const bandsLoading = bandsRes.status === 'loading' && bands === null;

  return (
    <>
      {membershipLoading || bandsLoading ? (
        <Skeleton variant="rounded" height={240} role="status" />
      ) : null}

      {membershipRes.status === 'error' && !membershipUnknown ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={membershipRes.refetch}>
              {t('common.retry', 'Retry')}
            </Button>
          }
        >
          {t('config.membership.loadError', 'Could not load membership settings.')}
          <ErrorDetails
            intent="read"
            error={membershipRes.error}
            headline={t('config.membership.loadError', 'Could not load membership settings.')}
          />
        </Alert>
      ) : null}

      {bandsRes.status === 'error' && !bandsUnknown ? (
        <Alert
          severity="error"
          sx={{ mt: membershipRes.status === 'error' && !membershipUnknown ? 2 : 0 }}
          action={
            <Button color="inherit" size="small" onClick={bandsRes.refetch}>
              {t('common.retry', 'Retry')}
            </Button>
          }
        >
          {t('config.bands.loadError', 'Could not load attendance bands.')}
          <ErrorDetails
            intent="read"
            error={bandsRes.error}
            headline={t('config.bands.loadError', 'Could not load attendance bands.')}
          />
        </Alert>
      ) : null}

      {membership !== null && !(membershipRes.status === 'error' && !membershipUnknown) ? (
        <MembershipSettings data={membership} onSaved={setMembership} />
      ) : null}

      {bands !== null && !(bandsRes.status === 'error' && !bandsUnknown) ? (
        <AttendanceBandsSettings data={bands} onSaved={setBands} />
      ) : null}

      <TimezoneSettings onUnknownTenant={setTimezoneUnknown} />
    </>
  );
}
